import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { Lock } from '@material-ui/icons';
import LockOpenOutlined from '@material-ui/icons/LockOpenOutlined';
import { RootState } from '@modules/index';
import { RegisterRfqInput } from '@generated/graphql';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import FormInputLayout from '../Layout/FormInputLayout';

const PublicOpenControl: React.FC = () => {
  const publicOpenDefaultValue = useSelector<RootState>(
    (state) => state.request.publicOpen
  ) as boolean;
  const { controlContainer } = useStyles();
  const [publicOpen, setPublicOpen] = useState(publicOpenDefaultValue ?? true);
  const { control } = useFormContext<RegisterRfqInput>();

  return (
    <FormInputLayout label="도면 보안설정" required={true}>
      <Controller
        control={control}
        name="publicOpen"
        defaultValue={publicOpenDefaultValue ?? true}
        render={({ onChange }) => (
          <div className={controlContainer}>
            <div
              data-cy="rfq-publicOpen-true"
              className={clsx([
                'switch',
                {
                  active: publicOpen
                }
              ])}
              onClick={(e) => {
                setPublicOpen(true);
                onChange(true);
              }}
            >
              <LockOpenOutlined className="public-open-true-icon" />
              <div>
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  {`모든 파트너가
            도면 다운로드 가능`}
                </CustomizedTypography>

                <CustomizedTypography
                  variant="body2"
                  color="textSecondary"
                  noWrap
                >
                  더 정확한 견적을 받을 수 있어요.
                </CustomizedTypography>
              </div>
            </div>
            <div
              data-cy="rfq-publicOpen-false"
              className={clsx([
                'switch',
                {
                  active: !publicOpen
                }
              ])}
              onClick={(e) => {
                setPublicOpen(false);
                onChange(false);
              }}
            >
              <Lock className="public-open-false-icon" />
              <div>
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                >
                  {`허용된 파트너만
            도면 다운로드 가능`}
                </CustomizedTypography>

                <CustomizedTypography
                  variant="body2"
                  color="textSecondary"
                  noWrap
                >
                  신뢰하는 파트너에게만 다운로드 권한을 부여합니다.
                </CustomizedTypography>
              </div>
            </div>
          </div>
        )}
      />
    </FormInputLayout>
  );
};

export default PublicOpenControl;

const useStyles = makeStyles((theme) => ({
  controlContainer: {
    display: 'flex',
    gap: '16px',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column'
    },
    '& .switch': {
      display: 'flex',
      width: '50%',
      flexDirection: 'column',
      padding: '20px 18px',
      borderRadius: '12px',
      border: `1px solid ${colorSet.gray400}`,
      backgroundColor: colorSet.gray000,
      gap: '12px',
      '& p': {
        whiteSpace: 'initial'
      },
      '& .public-open-true-icon': {
        color: colorSet.gray900
      },
      '& .public-open-false-icon': {
        color: colorSet.gray900
      },
      [theme.breakpoints.down('md')]: {
        flexDirection: 'row',
        alignItems: 'center'
      },
      '&:hover': {
        cursor: 'pointer'
      },
      '&.active': {
        backgroundColor: colorSet.gray100,
        '& .public-open-true-icon': {
          color: colorSet.successBase
        },
        '& .public-open-false-icon': {
          color: colorSet.errorBase
        }
      },
      '&:hover, &.active': {
        border: `2px solid ${colorSet.gray900}`,
        padding: '19px 17px'
      }
    }
  }
}));
